import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Send, Package, MapPin, User, Users, ClipboardList, Clock, ShieldAlert, Eye, Pencil, Download } from 'lucide-react';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { Button } from '../../components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { toSydneyDate, formatTimestamp } from '../../utils/helpers';

interface IssuanceItem {
    id: string;
    inventoryItem: {
        id: string;
        name: string;
        sku: string;
        costPerUnit: number;
        currentStock: number;
        category: string;
    };
    quantity: number;
    unitCost: number;
    totalCost: number;
    expiryDate?: string;
}

interface Issuance {
    id: string;
    location: { id: string; name: string };
    workerType: 'System User' | 'Field Worker';
    systemUser?: { id: string; name: string };
    fieldWorker?: { id: string; name: string };
    notes: string;
    totalItems: number;
    totalValue: number;
    issuedBy: { id: string; name: string };
    issuedAt: string;
    items: IssuanceItem[];
    signatureType?: 'Draw' | 'Type';
    signatureData?: string;
}

const InventoryIssuance: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const [issuances, setIssuances] = useState<Issuance[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [selectedIssuance, setSelectedIssuance] = useState<Issuance | null>(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    useEffect(() => {
        fetchIssuances();
    }, []);

    const fetchIssuances = async () => {
        try {
            setLoading(true);
            const data = await api.get<Issuance[]>('/api/inventory/issuances');
            setIssuances(data);
        } catch (error) {
            console.error('Error fetching issuances:', error);
        } finally {
            setLoading(false);
        }
    };


    const getRecipientName = (issuance: Issuance) => {
        if (issuance.workerType === 'System User' && issuance.systemUser) {
            return issuance.systemUser.name;
        } else if (issuance.workerType === 'Field Worker' && issuance.fieldWorker) {
            return issuance.fieldWorker.name;
        }
        return 'Unknown';
    };

    const getIssuanceStatus = (issuance: Issuance) => {
        const now = new Date();
        const items = issuance.items || [];

        let hasExpired = false;
        let hasExpiringSoon = false;
        let hasTracked = false;

        items.forEach(item => {
            if (item.expiryDate) {
                hasTracked = true;
                const expiry = new Date(item.expiryDate);
                if (expiry < now) {
                    hasExpired = true;
                } else {
                    const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    if (diffDays <= 7) {
                        hasExpiringSoon = true;
                    }
                }
            }
        });

        if (hasExpired) return { label: 'Expired', color: 'bg-red-50 text-red-600 border-red-100' };
        if (hasExpiringSoon) return { label: 'Re-issue Soon', color: 'bg-orange-50 text-orange-600 border-orange-100' };
        if (hasTracked) return { label: 'Active', color: 'bg-green-50 text-green-600 border-green-100' };
        return { label: 'Permanent', color: 'bg-gray-50 text-gray-500 border-gray-100' };
    };

    const downloadCSV = () => {
        const headers = ['Issuance ID', 'Recipient Type', 'Recipient Name', 'Location', 'Items Count', 'Issued Items', 'Total Value', 'Date & Time', 'Status', 'Issued By', 'Notes'];
        const rows = issuances.map(issuance => {
            const recipientName = getRecipientName(issuance);
            const status = getIssuanceStatus(issuance).label;
            const issuedItems = issuance.items?.map(item => `${item.quantity}x ${item.inventoryItem?.name || 'Unknown Item'}`).join(', ') || '';

            return [
                issuance.id,
                issuance.workerType,
                recipientName,
                issuance.location?.name || '',
                issuance.totalItems,
                issuedItems,
                Number(issuance.totalValue).toFixed(2),
                formatTimestamp(issuance.issuedAt),
                status,
                issuance.issuedBy?.name || '',
                issuance.notes || ''
            ];
        });

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `issuance-records-${toSydneyDate(new Date().toISOString()).replace(/\//g, '-')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (!ability.can(ACTIONS.READ, RESOURCES.INVENTORY_ISSUANCE)) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6">
                    <ShieldAlert size={40} className="text-red-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-500 max-w-sm">You do not have permission to view inventory issuance records.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {issuances.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-20 flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-6">
                        <Send size={40} className="text-gray-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Issuance Flow</h3>
                    <p className="text-gray-500 mb-8 max-w-sm">Create new issuances to field workers and system users</p>
                    {ability.can(ACTIONS.CREATE, RESOURCES.INVENTORY_ISSUANCE) && (
                        <Button
                            onClick={() => navigate('/inventory/issuances/new')}
                            className="bg-black hover:bg-slate-800 text-white rounded-xl px-8 h-12 flex items-center gap-2"
                        >
                            <Plus size={18} />
                            Create New Issuance
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                        <h2 className="text-xl font-bold text-gray-900">Issuance Records</h2>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <Button
                                variant="outline"
                                className="border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 flex-1 sm:flex-none h-10 px-4 rounded-xl"
                                onClick={downloadCSV}
                            >
                                <Download size={16} />
                                <span className="hidden sm:inline font-bold">Export CSV</span>
                                <span className="sm:hidden font-bold">CSV</span>
                            </Button>
                            {ability.can(ACTIONS.CREATE, RESOURCES.INVENTORY_ISSUANCE) && (
                                <Button
                                    onClick={() => navigate('/inventory/issuances/new')}
                                    className="bg-black hover:bg-slate-800 text-white rounded-xl flex items-center justify-center gap-2 flex-1 sm:flex-none h-10 px-4"
                                >
                                    <Plus size={18} />
                                    New Issuance
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="space-y-4">
                        {/* Desktop Table View */}
                        <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                            <Table>
                                <TableHeader className="bg-gray-50/50">
                                    <TableRow>
                                        <TableHead className="font-bold text-gray-400">Recipient</TableHead>
                                        <TableHead className="font-bold text-gray-400">Location</TableHead>
                                        <TableHead className="font-bold text-gray-400 text-center">Items</TableHead>
                                        <TableHead className="font-bold text-gray-400 text-right">Total Value</TableHead>
                                        <TableHead className="font-bold text-gray-400">Date & Time</TableHead>
                                        <TableHead className="font-bold text-gray-400">Status</TableHead>
                                        <TableHead className="font-bold text-gray-400">Issued By</TableHead>
                                        <TableHead className="font-bold text-gray-400 text-center">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {issuances.map((issuance) => (
                                        <TableRow
                                            key={issuance.id}
                                            className="hover:bg-gray-50/80 transition-all group"
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                                                        {issuance.workerType === 'Field Worker' ? <Users size={14} /> : <User size={14} />}
                                                    </div>
                                                    <span className="font-bold text-sm text-gray-800">{getRecipientName(issuance)}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm font-medium text-gray-600">{issuance.location.name}</TableCell>
                                            <TableCell className="text-center">
                                                <Badge variant="outline" className="rounded-lg font-bold bg-white">
                                                    {issuance.totalItems}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right font-black text-sm text-gray-900">
                                                ${Number(issuance.totalValue).toFixed(2)}
                                            </TableCell>
                                                <TableCell className="text-sm font-medium text-gray-500">{formatTimestamp(issuance.issuedAt)}</TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const status = getIssuanceStatus(issuance);
                                                    return (
                                                        <Badge variant="outline" className={cn("rounded-lg font-bold border whitespace-nowrap", status.color)}>
                                                            {status.label}
                                                        </Badge>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-600 font-bold">
                                                {issuance.issuedBy?.name}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center justify-center gap-1">
                                                    <Button
                                                        onClick={() => {
                                                            setSelectedIssuance(issuance);
                                                            setIsViewModalOpen(true);
                                                        }}
                                                        className="w-8 h-8 rounded-lg border-gray-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all shadow-sm"
                                                        title="View Details"
                                                        variant="outline"
                                                        size="icon"
                                                    >
                                                        <Eye size={14} />
                                                    </Button>
                                                    {ability.can(ACTIONS.UPDATE, RESOURCES.INVENTORY_ISSUANCE) && (
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => navigate(`/inventory/issuances/${issuance.id}/edit`)}
                                                            className="w-8 h-8 rounded-lg border-gray-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all shadow-sm"
                                                            title="Edit Issuance"
                                                        >
                                                            <Pencil size={14} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden space-y-4">
                            {issuances.map((issuance) => {
                                const status = getIssuanceStatus(issuance);
                                return (
                                    <div
                                        key={issuance.id}
                                        className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                                                    {issuance.workerType === 'Field Worker' ? <Users size={16} /> : <User size={16} />}
                                                </div>
                                                <div>
                                                    <span className="font-bold text-gray-800 block">{getRecipientName(issuance)}</span>
                                                    <span className="text-xs text-gray-400 font-medium">{formatTimestamp(issuance.issuedAt)}</span>
                                                </div>
                                            </div>
                                            <Badge variant="outline" className={cn("rounded-lg font-bold border whitespace-nowrap", status.color)}>
                                                {status.label}
                                            </Badge>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 py-3 border-y border-gray-50">
                                            <div>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Items</p>
                                                <p className="font-bold text-gray-900">{issuance.totalItems} Units</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Value</p>
                                                <p className="font-black text-gray-900">${Number(issuance.totalValue).toFixed(2)}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                                                <MapPin size={12} className="text-gray-300" />
                                                {issuance.location.name}
                                            </div>
                                            <div className="text-[10px] font-bold text-gray-400 italic">
                                                By: {issuance.issuedBy?.name}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 pt-1">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="flex-1 rounded-xl h-9 text-xs font-bold gap-1.5 border-gray-100"
                                                onClick={() => {
                                                    setSelectedIssuance(issuance);
                                                    setIsViewModalOpen(true);
                                                }}
                                            >
                                                <Eye size={14} />
                                                View
                                            </Button>
                                            {ability.can(ACTIONS.UPDATE, RESOURCES.INVENTORY_ISSUANCE) && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="flex-1 rounded-xl h-9 text-xs font-bold gap-1.5 border-gray-100"
                                                    onClick={() => navigate(`/inventory/issuances/${issuance.id}/edit`)}
                                                >
                                                    <Pencil size={14} />
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            {isViewModalOpen && selectedIssuance && (
                <ViewIssuanceModal
                    isOpen={isViewModalOpen}
                    onClose={() => {
                        setIsViewModalOpen(false);
                        setSelectedIssuance(null);
                    }}
                    issuance={selectedIssuance}
                />
            )}
        </div>
    );
};

// --- View Issuance Modal ---
export const ViewIssuanceModal = ({ isOpen, onClose, issuance }: any) => {
    if (!issuance) return null;

    const recipientName = issuance.workerType === 'System User'
        ? issuance.systemUser?.name
        : issuance.fieldWorker?.name;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl rounded-3xl p-0 overflow-hidden border-none shadow-2xl max-h-[95vh] flex flex-col">
                <div className="p-8 bg-slate-900 text-white relative overflow-hidden shrink-0">
                    <div className="absolute top-0 right-0 p-8 opacity-10 rotate-12">
                        <ClipboardList size={120} strokeWidth={1} />
                    </div>
                    <div className="relative z-10">
                        <Badge className="bg-white/20 text-white border-0 mb-4 hover:bg-white/30 backdrop-blur-md">
                            Issuance Details
                        </Badge>
                        <h3 className="text-2xl font-bold mb-1">Stock Issuance Report</h3>
                    </div>
                </div>

                <div className="p-6 sm:p-8 space-y-6 sm:space-y-8 bg-white overflow-y-auto flex-1 custom-scrollbar">
                    {/* Header Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 pb-8 border-b border-gray-50">
                        <div className="space-y-4">
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Recipient</p>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                                        {issuance.workerType === 'Field Worker' ? <Users size={14} /> : <User size={14} />}
                                    </div>
                                    <span className="font-bold text-gray-900">{recipientName}</span>
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Location</p>
                                <div className="flex items-center gap-3 text-gray-700">
                                    <MapPin size={14} className="text-gray-400" />
                                    <span className="font-bold">{issuance.location?.name}</span>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Issued By</p>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 shrink-0 border border-gray-100/50">
                                        <User size={18} />
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="font-bold text-gray-900 text-sm leading-tight">{issuance.issuedBy?.name}</span>
                                        <div className="flex items-center gap-1.5 text-slate-400 mt-1">
                                            <Clock size={10} strokeWidth={2.5} />
                                            <span className="text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
                                                {formatTimestamp(issuance.issuedAt)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Summary</p>
                                <p className="font-bold text-gray-900 text-sm">
                                    {issuance.totalItems} Items • ${Number(issuance.totalValue).toFixed(2)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Item List */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Itemized Breakdown</h4>
                        <div className="space-y-3">
                            {issuance.items.map((item: any) => (
                                <div key={item.id} className="flex items-center justify-between p-4 rounded-2xl bg-gray-50/50 border border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-slate-400">
                                            <Package size={20} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-sm text-gray-900">{item.inventoryItem?.name}</p>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                {item.inventoryItem?.sku} • ${Number(item.unitCost).toFixed(2)} each
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-black text-sm text-gray-900">x{item.quantity}</p>
                                        <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest">${Number(item.totalCost).toFixed(2)}</p>
                                        {item.expiryDate && (
                                            <p className="text-[9px] font-bold text-orange-500 mt-1 uppercase tracking-tighter">
                                                Expires: {toSydneyDate(item.expiryDate)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Signature */}
                    {issuance.signatureData && (
                        <div className="space-y-4 pt-6 border-t border-gray-50">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Worker Signature</p>
                            <div className="p-4 rounded-2xl bg-gray-50/50 border border-gray-100">
                                {issuance.signatureType === 'Draw' ? (
                                    <img src={issuance.signatureData} alt="Signature" className="max-h-20" />
                                ) : (
                                    <div className="font-signature text-2xl text-gray-800">
                                        {issuance.signatureData}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Notes */}
                    {issuance.notes && (
                        <div className="space-y-2 p-4 rounded-2xl bg-orange-50/30 border border-orange-100">
                            <p className="text-[10px] font-bold text-orange-600 uppercase tracking-widest">Notes</p>
                            <p className="text-sm text-gray-700 font-medium leading-relaxed">{issuance.notes}</p>
                        </div>
                    )}
                </div>

                <div className="p-6 bg-gray-50/50 border-t border-gray-50 flex justify-end shrink-0">
                    <Button onClick={onClose} className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-11 px-8 font-bold transition-all">
                        Close Details
                    </Button>
                </div>
            </DialogContent>
        </Dialog >
    );
};

export default InventoryIssuance;
